/**
 * Build monthly schedules for parcelamento (split total) or recorrência (same amount each month).
 * Dates are YYYY-MM-DD; uses noon local to avoid DST edge cases when shifting months.
 */

import type { TransactionRecurrencyEntry } from "../../../types/transaction";

export type ScheduleRowPreview = {
  index: number;
  dueDate: string;
  competenceDate: string | null;
  paymentDate: string | null;
  amount: number;
  installmentLabel: string | null;
};

/** Add calendar months to an ISO date (YYYY-MM-DD). */
export function addCalendarMonthsIso(isoDate: string, monthsToAdd: number): string {
  const trimmed = isoDate.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const d = new Date(`${trimmed}T12:00:00`);
  d.setMonth(d.getMonth() + monthsToAdd);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Split total BRL amount into `parts` chunks (cents-safe). */
export function splitAmountAcrossParts(total: number, parts: number): number[] {
  if (parts < 1) return [];
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / parts);
  const remainder = cents - base * parts;
  const out: number[] = [];
  for (let i = 0; i < parts; i++) {
    const c = base + (i < remainder ? 1 : 0);
    out.push(c / 100);
  }
  return out;
}

function optionalShifted(
  base: string,
  monthOffset: number,
  hasBase: boolean
): string | null {
  if (!hasBase || !base.trim()) return null;
  return addCalendarMonthsIso(base, monthOffset);
}

export function buildInstallmentPreview(
  totalAmount: number,
  count: number,
  baseDue: string,
  baseCompetence: string,
  basePayment: string
): ScheduleRowPreview[] | null {
  if (count < 2 || !baseDue.trim()) return null;
  const amounts = splitAmountAcrossParts(totalAmount, count);
  if (amounts.length !== count) return null;
  const hasComp = Boolean(baseCompetence.trim());
  const hasPay = Boolean(basePayment.trim());
  return amounts.map((amount, i) => ({
    index: i + 1,
    dueDate: addCalendarMonthsIso(baseDue, i),
    competenceDate: hasComp
      ? addCalendarMonthsIso(baseCompetence, i)
      : addCalendarMonthsIso(baseDue, i),
    paymentDate: optionalShifted(basePayment, i, hasPay),
    amount,
    installmentLabel: `${i + 1}/${count}`,
  }));
}

/**
 * Before submit: competence = vencimento, payment dates from form (+1 mês por parcela).
 * Preserves each row's amount (including user edits from revisão).
 */
export function finalizeInstallmentRowsForSubmit(
  draftRows: ScheduleRowPreview[],
  basePayment: string
): ScheduleRowPreview[] {
  const hasPay = Boolean(basePayment.trim());
  return draftRows.map((row, i) => ({
    ...row,
    index: i + 1,
    installmentLabel: `${i + 1}/${draftRows.length}`,
    competenceDate: row.dueDate,
    paymentDate: optionalShifted(basePayment, i, hasPay),
  }));
}

export function buildRecurrentPreview(
  amountEach: number,
  count: number,
  baseDue: string,
  baseCompetence: string,
  basePayment: string
): ScheduleRowPreview[] | null {
  if (count < 2 || !baseDue.trim()) return null;
  const hasComp = Boolean(baseCompetence.trim());
  const hasPay = Boolean(basePayment.trim());
  return Array.from({ length: count }, (_, i) => ({
    index: i + 1,
    dueDate: addCalendarMonthsIso(baseDue, i),
    competenceDate: hasComp
      ? addCalendarMonthsIso(baseCompetence, i)
      : addCalendarMonthsIso(baseDue, i),
    paymentDate: optionalShifted(basePayment, i, hasPay),
    amount: amountEach,
    installmentLabel: null,
  }));
}

/** Monta `recurrency.entries` a partir do preview de recorrência (POST único). */
export function buildRecurrencyEntriesFromScheduleRows(
  rows: ScheduleRowPreview[]
): TransactionRecurrencyEntry[] {
  return rows.map((row) => {
    const competenceDate = (row.competenceDate ?? row.dueDate).trim();
    const dueDate = row.dueDate.trim();
    const entry: TransactionRecurrencyEntry = { competenceDate, dueDate };
    const pay = row.paymentDate?.trim();
    if (pay) entry.paymentDate = pay;
    return entry;
  });
}
